import React, { useState, useEffect } from 'react'
import { Navigate, useNavigate, useLocation } from 'react-router-dom'
import {
  IconButton, Typography, Button,
} from '@mui/material'
import { useDispatch } from 'react-redux'
import classNames from 'classnames'
import Wrapper from '@app/components/Common/Wrapper/Wrapper'
import { Add, ScenesArrowBack, ReloadCircle } from 'icons'
import { DeviceTypes, IZwaveOrTwrDevice } from '@app/@types/myDevices'
import { setMyDevices } from '@app/actions/myDevices/myDevices'
import {
  COMMIT_TO_MEZZO, COMPLETE, DELETING, IN_PROGRESS, NOT_SAVED,
} from '@app/constants/mezzoAnswers'
import AddGroupPopup from '@app/components/Groups/GroupsList/AddGroupPopup/AddGroupPopup'
import {
  groupAddDevice, groupDeleteDevice, syncGroupsList,
} from '@app/actions/groups/groups'
import { IGroups, IGroup } from '@app/@types/groups'
import ErrorPopup from '@app/components/Groups/GroupsList/ErrorPopup/ErrorPopup'
import { maxGroupsNumber } from '@app/constants/groups/groups'
import useStyles from './DeviceGroups.style'

interface Props {
  groupsList: IGroups,
  admin: boolean,
  currentDevice: IZwaveOrTwrDevice,
  devices: Array<IZwaveOrTwrDevice>,
  isMobile: boolean,
}

const DeviceGroups: React.FC<Props> = (props) => {
  const {
    groupsList,
    admin,
    currentDevice,
    devices,
    isMobile,
  } = props
  const navigate = useNavigate()
  const location = useLocation()
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const [isAddPopupOpen, setIsAddPopupOpen] = useState<boolean>(false)
  const [isErrorPopupOpen, setIsErrorPopupOpen] = useState<boolean>(false)

  useEffect(() => {
    dispatch(syncGroupsList())
  }, [])

  useEffect(() => {
    setIsAddPopupOpen(false)
  }, [groupsList])

  const renderGroupStatus = (status: string): string => {
    switch (status) {
    case COMMIT_TO_MEZZO: return `(${I18n.t('geofence_web.saving')})`
    case IN_PROGRESS: return `(${I18n.t('geofence_web.saving')})`
    case NOT_SAVED: return `(${I18n.t('geofence_web.not_saved')})`
    case DELETING: return `(${I18n.t('geofence_web.deleting')})`
    case COMPLETE: return ''
    default: return ''
    }
  }

  const chooseOrDeleteDeviceHandler = (group: IGroup) => {
    if (currentDevice) {
      let updatedRooms: IZwaveOrTwrDevice['group']
      if (currentDevice.group?.id === group.id) {
        dispatch(groupDeleteDevice(currentDevice.id, currentDevice.device_type === DeviceTypes.zwaveDevice))
        updatedRooms = null
      } else {
        dispatch(groupAddDevice(currentDevice.id, group.id, currentDevice.device_type === DeviceTypes.zwaveDevice))
        updatedRooms = { id: group.id, name: group.name, number: group.number }
      }
      dispatch(setMyDevices(devices.map((device) => (
        device.id === currentDevice.id && device.device_type === currentDevice.device_type
          ? { ...device, group: updatedRooms }
          : device))))
    }
    goBackHandler()
  }

  const goBackHandler = () => navigate(-1)

  return (
    <>
      {currentDevice ? (
        <Wrapper className={classNames(classes.root, isMobile && classes.rootMobile)}>
          <Wrapper className={classes.navigationControls}>
            <Wrapper className={classes.navigationLeftSide}>
              <IconButton size="small" onClick={goBackHandler} className={classes.goBackIcon}>
                <ScenesArrowBack />
              </IconButton>
              <Typography className={classes.navigationTitle}>{I18n.t('rooms_and_groups.my_groups')}</Typography>
            </Wrapper>
            <IconButton
              onClick={() => {
                dispatch(syncGroupsList())
              }}
            >
              <ReloadCircle />
            </IconButton>
          </Wrapper>
          {groupsList.length !== 0 ? (
            <Wrapper className={classes.grid}>
              {groupsList.map((group, index) => (
                <Wrapper className={classes.gridElement} key={`group-${index + 1}`}>
                  <Typography>{`${group.name} ${group.status ? renderGroupStatus(group.status) : ''}`}</Typography>
                  {group.status === COMPLETE && (
                    <Button
                      color="primary"
                      className={classes.button}
                      variant="text"
                      onClick={() => {
                        chooseOrDeleteDeviceHandler(group)
                      }}
                    >
                      {currentDevice.group?.id === group.id
                        ? `${I18n.t('buttons.delete')}`
                        : `${I18n.t('buttons.choose')}`}
                    </Button>
                  )}
                </Wrapper>
              ))}
            </Wrapper>
          ) : (
            <Typography className={classes.emptyError}>{I18n.t('rooms_and_groups.no_groups')}</Typography>
          )}
          <AddGroupPopup
            open={isAddPopupOpen}
            names={groupsList.map((group) => group.name)}
            close={() => {
              setIsAddPopupOpen(false)
            }}
          />
          <ErrorPopup
            open={isErrorPopupOpen}
            close={() => {
              setIsErrorPopupOpen(!isErrorPopupOpen)
            }}
          />
          {admin && (
            <IconButton
              aria-label="add button"
              className={classNames(classes.addButton, classes.initialAddButton)}
              onClick={() => {
                if (groupsList.length === maxGroupsNumber) {
                  setIsErrorPopupOpen(!isErrorPopupOpen)
                } else {
                  setIsAddPopupOpen(true)
                }
              }}
              size="large"
            >
              <Add />
            </IconButton>
          )}
        </Wrapper>
      ) : <Navigate to={location.pathname.split('/').slice(0, -1).join('/')} />}
    </>
  )
}

export default DeviceGroups
